import {
  Children,
  type ComponentProps,
  cloneElement,
  createContext,
  type InputHTMLAttributes,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  useContext,
  useId,
} from 'react'
import { cx } from '../cx'
import { Icon } from '../Icon/Icon'
import styles from './Field.module.css'

/**
 * Label + control + optional help and error. The generic wrapper behind the
 * typed fields.
 *
 * Pass `id` to pin the association, or omit it to get a stable generated id.
 * Either way the label's `htmlFor` and the control's `id` resolve to the same
 * value. The control must be the first child; it receives the id,
 * `aria-describedby` (its own value, then help, then error), `aria-invalid`,
 * `readOnly` and `required`. Later children pass through untouched.
 */
export function Field({
  id,
  label,
  hint,
  help,
  error,
  readOnly,
  required,
  className,
  children,
}: {
  id?: string
  label: ReactNode
  hint?: ReactNode
  help?: ReactNode
  error?: ReactNode
  readOnly?: boolean
  required?: boolean
  className?: string
  children: ReactNode
}) {
  const generatedId = useId()
  const fieldId = id ?? generatedId
  // `Children.toArray` drops falsy children, so the first real element is the
  // control even when a conditional sibling is currently false.
  const [first, ...rest] = Children.toArray(children)
  const describedBy = [
    isValidElement(first)
      ? (first.props as Record<string, unknown>)['aria-describedby']
      : undefined,
    help != null ? `${fieldId}-help` : undefined,
    error != null ? `${fieldId}-error` : undefined,
  ]
    .filter(Boolean)
    .join(' ')
  const decorated = [
    isValidElement(first)
      ? cloneElement(first as ReactElement<Record<string, unknown>>, {
          id: fieldId,
          ...(describedBy ? { 'aria-describedby': describedBy } : {}),
          ...(error != null ? { 'aria-invalid': true } : {}),
          ...(readOnly ? { readOnly: true, 'aria-readonly': true } : {}),
          ...(required ? { required: true } : {}),
        })
      : first,
    ...rest,
  ]
  return (
    <div className={cx(styles.field, className)}>
      <label className={styles.label} htmlFor={fieldId}>
        {label}
        {hint != null && (
          <>
            {' '}
            <span className={styles.hint}>{hint}</span>
          </>
        )}
      </label>
      {decorated}
      {help != null && (
        <p id={`${fieldId}-help`} className={styles.help}>
          {help}
        </p>
      )}
      {error != null && (
        <p id={`${fieldId}-error`} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

/** `mono` sets a machine value (an id, a cron expression, JSON) in the code
 * face, as the Field preview's workspace id does (D57). */
type MonoProp = { mono?: boolean }

/** Props are `ComponentProps<'input'>`, so a `ref` is forwarded (React 19). */
export function Input({
  mono,
  className,
  ...props
}: ComponentProps<'input'> & MonoProp) {
  return (
    <input
      {...props}
      className={cx(styles.control, className)}
      data-mono={mono || undefined}
    />
  )
}

export function Textarea({
  mono,
  className,
  ...props
}: ComponentProps<'textarea'> & MonoProp) {
  return (
    <textarea
      rows={3}
      {...props}
      className={cx(styles.control, className)}
      data-mono={mono || undefined}
    />
  )
}

export function Select(props: ComponentProps<'select'>) {
  return (
    <span className={styles.select}>
      <select {...props} className={cx(styles.control, props.className)} />
      <Icon name="chevron-down" size="icon-embedded" className={styles.caret} />
    </span>
  )
}

export function Checkbox({
  indeterminate,
  className,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  indeterminate?: boolean
}) {
  return (
    <input
      type="checkbox"
      {...rest}
      ref={(el) => {
        if (el) el.indeterminate = Boolean(indeterminate)
      }}
      className={cx(styles.checkbox, className)}
    />
  )
}

interface FieldShellProps {
  id: string
  label: string
  hint?: ReactNode
  help?: ReactNode
  error?: ReactNode
  readOnly?: boolean
  required?: boolean
  value: string
  onValue: (value: string) => void
}

export function TextField({
  id,
  label,
  hint,
  help,
  error,
  readOnly,
  required,
  value,
  onValue,
  inputProps,
}: FieldShellProps & {
  inputProps?: Omit<
    ComponentProps<typeof Input>,
    'id' | 'value' | 'onChange' | 'ref'
  >
}) {
  return (
    <Field
      id={id}
      label={label}
      hint={hint}
      help={help}
      error={error}
      readOnly={readOnly}
      required={required}
    >
      <Input
        value={value}
        onChange={(e) => onValue(e.target.value)}
        {...inputProps}
      />
    </Field>
  )
}

/** Multi-line text, starting at three lines. Use it where pasted newlines are
 * significant (for example PEM private keys, which are invalid if flattened). */
export function TextareaField({
  id,
  label,
  hint,
  help,
  error,
  readOnly,
  required,
  value,
  onValue,
  textareaProps,
}: FieldShellProps & {
  textareaProps?: Omit<
    ComponentProps<typeof Textarea>,
    'id' | 'value' | 'onChange' | 'ref'
  >
}) {
  return (
    <Field
      id={id}
      label={label}
      hint={hint}
      help={help}
      error={error}
      readOnly={readOnly}
      required={required}
    >
      <Textarea
        value={value}
        onChange={(e) => onValue(e.target.value)}
        {...textareaProps}
      />
    </Field>
  )
}

export function SelectField({
  id,
  label,
  hint,
  help,
  error,
  readOnly,
  required,
  value,
  onValue,
  children,
  selectProps,
}: FieldShellProps & {
  /** `<option>` elements. */
  children: ReactNode
  selectProps?: Omit<
    SelectHTMLAttributes<HTMLSelectElement>,
    'id' | 'value' | 'onChange'
  >
}) {
  return (
    <Field
      id={id}
      label={label}
      hint={hint}
      help={help}
      error={error}
      readOnly={readOnly}
      required={required}
    >
      <Select
        value={value}
        onChange={(e) => onValue(e.target.value)}
        {...selectProps}
      >
        {children}
      </Select>
    </Field>
  )
}

/** Set by a locked `CheckboxGroup`: its checkboxes are `aria-disabled` and ignore activation. */
const CheckboxLockContext = createContext(false)

export function CheckboxField({
  id,
  label,
  checked,
  onChecked,
  indeterminate,
  ground = 'page',
  inputProps,
}: {
  id: string
  label: ReactNode
  checked: boolean
  onChecked: (checked: boolean) => void
  indeterminate?: boolean
  /** `surface` (a card, an overlay) hovers and presses to raise (D190). */
  ground?: 'page' | 'surface'
  inputProps?: Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'id' | 'type' | 'checked' | 'onChange'
  >
}) {
  const locked = useContext(CheckboxLockContext)
  return (
    <label className={styles.check} htmlFor={id} data-ground={ground}>
      <Checkbox
        id={id}
        checked={checked}
        indeterminate={indeterminate}
        aria-disabled={locked || undefined}
        onChange={(e) => {
          // Controlled: ignoring the change lets React restore `checked`.
          if (!locked) onChecked(e.target.checked)
        }}
        {...inputProps}
      />
      <span>{label}</span>
    </label>
  )
}

/**
 * A set of `CheckboxField`s under one legend; `disabled` disables the set
 * natively (out of the tab order). `locked` is the dependency lock
 * (`component-states`): the checkboxes are `aria-disabled`, stay focusable and
 * ignore activation; `lockedReason` shows under the legend while locked.
 */
export function CheckboxGroup({
  label,
  help,
  disabled,
  locked = false,
  lockedReason,
  className,
  children,
}: {
  label: ReactNode
  help?: ReactNode
  disabled?: boolean
  locked?: boolean
  lockedReason?: ReactNode
  className?: string
  children: ReactNode
}) {
  const helpId = useId()
  const reasonId = useId()
  const showReason = locked && lockedReason != null
  const describedBy = [help != null && helpId, showReason && reasonId]
    .filter(Boolean)
    .join(' ')
  return (
    <fieldset
      className={cx(styles.group, className)}
      disabled={disabled}
      aria-disabled={locked || undefined}
      aria-describedby={describedBy || undefined}
    >
      <legend className={cx(styles.label, styles.legend)}>{label}</legend>
      {help != null && (
        <p id={helpId} className={styles.help}>
          {help}
        </p>
      )}
      {showReason && (
        <p id={reasonId} className={styles.help}>
          {lockedReason}
        </p>
      )}
      <CheckboxLockContext.Provider value={locked}>
        <div className={styles.options}>{children}</div>
      </CheckboxLockContext.Provider>
    </fieldset>
  )
}
